import { useState, useEffect, useRef } from 'react';

export interface CriterionScore {
  criterionName: string;
  score: number;
  weight: number;
}

export interface CompositeScore {
  modelId: string;
  modelName: string;
  modelDisplayName?: string;
  compositeScore: number | null;
  criterionScores: CriterionScore[];
}

interface ScoresResponse {
  compositeScores?: CompositeScore[];
  results?: any;
  featureEnabled?: boolean;
  message?: string;
}

interface UseComparisonScoresResult {
  scores: CompositeScore[] | null;
  loading: boolean;
  error: string | null;
  featureEnabled: boolean;
  refetch: () => void;
  refetchAndWait: () => Promise<CompositeScore[] | null>;
}

/**
 * useComparisonScores - React hook for fetching LLM judge scores
 * LLM Judge scoring is now always enabled (hardcoded)
 *
 * Caches results in memory to avoid redundant API calls
 */
export function useComparisonScores(comparisonId: string | undefined): UseComparisonScoresResult {
  const [scores, setScores] = useState<CompositeScore[] | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [refetchTrigger, setRefetchTrigger] = useState(0);

  // Track pending refetch promises
  const refetchResolverRef = useRef<((scores: CompositeScore[] | null) => void) | null>(null);

  // LLM Judge scoring is always enabled (hardcoded)
  const featureEnabled = true;

  console.log('[useComparisonScores] Hook called:', {
    comparisonId,
    featureEnabled,
    currentScores: scores?.length || 0
  });

  useEffect(() => {
    // Don't fetch if no comparison ID
    if (!comparisonId) {
      console.log('[useComparisonScores] Not fetching - no comparisonId');
      setScores(null);
      setLoading(false);
      setError(null);
      return;
    }

    let cancelled = false;

    async function fetchScores() {
      console.log('[useComparisonScores] Starting fetch for:', comparisonId);
      setLoading(true);
      setError(null);

      let fetchedScores: CompositeScore[] | null = null;

      try {
        const url = `/api/results/${comparisonId}/scores?includeComposite=true&_=${Date.now()}`;
        console.log('[useComparisonScores] Fetching:', url);

        const response = await fetch(url, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
            'Content-Type': 'application/json'
          }
        });

        console.log('[useComparisonScores] Response status:', response.status);

        if (!response.ok) {
          if (response.status === 404) {
            console.log('[useComparisonScores] 404 - No scores found');
            if (!cancelled) {
              setScores(null);
              setError(null);
            }
            return;
          }
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }

        const data: ScoresResponse = await response.json();
        console.log('[useComparisonScores] API response:', {
          featureEnabled: data.featureEnabled,
          hasCompositeScores: !!data.compositeScores,
          scoresCount: data.compositeScores?.length || 0,
          sampleScore: data.compositeScores?.[0]
        });

        if (!cancelled) {
          if (data.featureEnabled === false) {
            console.log('[useComparisonScores] Feature disabled on backend');
            setScores(null);
            setError(null);
          } else if (data.compositeScores) {
            console.log('[useComparisonScores] Setting scores:', data.compositeScores.length);
            fetchedScores = data.compositeScores;
            setScores(data.compositeScores);
            setError(null);
          } else {
            console.log('[useComparisonScores] No compositeScores in response');
            setScores(null);
            setError(null);
          }
        }
      } catch (err) {
        console.error('[useComparisonScores] Fetch error:', err);
        if (!cancelled) {
          setScores(null);
          setError(null);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
          // Resolve any pending refetch promise
          if (refetchResolverRef.current) {
            refetchResolverRef.current(fetchedScores);
            refetchResolverRef.current = null;
          }
        }
      }
    }

    fetchScores();

    return () => {
      cancelled = true;
    };
  }, [comparisonId, featureEnabled, refetchTrigger]);

  console.log('[useComparisonScores] Returning:', {
    scoresCount: scores?.length || 0,
    loading,
    error,
    featureEnabled
  });

  const refetchAndWait = () => {
    return new Promise<CompositeScore[] | null>((resolve) => {
      refetchResolverRef.current = resolve;
      setRefetchTrigger(prev => prev + 1);
    });
  };

  return {
    scores,
    loading,
    error,
    featureEnabled,
    refetch: () => setRefetchTrigger(prev => prev + 1),
    refetchAndWait
  };
}
